/**
 * KinsCircle Phase 6D — Legacy Viewer (owner/admin only).
 *
 * STABILITY.md §13–§14:
 *   - Owner/admin gated. Non-owner non-admin visitors are bounced to "/".
 *   - READ-ONLY preview surface for the Legacy Presentation Layer.
 *   - Includes per-flag toggles (browser-only) so an admin can locally
 *     test the cards / playback without flipping VITE_ENABLE_AI_* envs.
 *   - All flags persist via localStorage only — never to the database.
 *   - Hosts live previews of the public-facing 6D cards in admin context
 *     so we can verify they render null when their dependent flag is off.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Film,
  Sparkles,
  ShieldCheck,
  PlayCircle,
  ArrowLeft,
  AlertTriangle,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  setAIFlagLocal,
  snapshotLegacyFlags,
  isLegacyTimelineEnabled,
  isStoryPlaybackEnabled,
  isLegacyViewerEnabled,
} from '@/lib/ai/featureFlag';
import {
  buildFamilyJourney,
  buildLegacyTimeline,
  buildMilestoneSequence,
  buildYearHighlights,
  groupTimelineByEra,
  groupTimelineByPerson,
  groupTimelineByTheme,
  type FamilyJourney,
  type LegacyTimeline,
  type YearHighlight,
} from '@/lib/ai/legacyTimelineEngine';
import StoryPlaybackModal, {
  type PlaybackStory,
} from '@/components/ai/StoryPlaybackModal';
import RelationshipJourneyCard from '@/components/ai/RelationshipJourneyCard';
import FamilyDocumentaryCard from '@/components/ai/FamilyDocumentaryCard';
import MemorySpotlightCard from '@/components/ai/MemorySpotlightCard';

const LEGACY_FLAGS: {
  key: keyof ReturnType<typeof snapshotLegacyFlags>;
  label: string;
  description: string;
}[] = [
  {
    key: 'legacyTimeline',
    label: 'Legacy Timeline (master)',
    description:
      'Master flag for Phase 6D. All other Legacy flags require this to be ON.',
  },
  {
    key: 'storyPlayback',
    label: 'Story Playback Modal',
    description: 'Cinematic full-screen playback with auto-advance + manual nav.',
  },
  {
    key: 'relationshipJourney',
    label: 'Relationship Journey Card',
    description:
      'Per-relative timeline card. Requires ≥3 shared memories per person.',
  },
  {
    key: 'familyDocumentary',
    label: 'Family Documentary Card',
    description: 'Top-level family journey hero with eras, milestones, themes.',
  },
  {
    key: 'memorySpotlight',
    label: 'Memory Spotlight Card',
    description: 'Hero card highlighting one standout family memory.',
  },
  {
    key: 'legacyViewer',
    label: 'Legacy Viewer (this page)',
    description:
      'Required to render the cards inline below. The page itself stays accessible to owner/admin even when this is off.',
  },
];

const LegacyViewer: React.FC = () => {
  const navigate = useNavigate();
  const { isOwner, isAdmin, isAuthenticated } = useAuth();

  // Owner/admin gate. Members get bounced.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!isOwner && !isAdmin) navigate('/', { replace: true });
  }, [isAuthenticated, isOwner, isAdmin, navigate]);

  const [flags, setFlags] = useState(() => snapshotLegacyFlags());
  const refreshFlags = () => setFlags(snapshotLegacyFlags());

  const journey = useMemo<FamilyJourney | null>(() => {
    if (!isLegacyTimelineEnabled()) return null;
    return buildFamilyJourney();
  }, [flags]);

  const eras = useMemo<LegacyTimeline[]>(
    () => groupTimelineByEra({ maxTimelines: 6 }),
    [flags],
  );
  const themes = useMemo<LegacyTimeline[]>(
    () => groupTimelineByTheme({ maxTimelines: 6 }),
    [flags],
  );
  const people = useMemo<LegacyTimeline[]>(
    () => groupTimelineByPerson({ maxTimelines: 6 }),
    [flags],
  );
  const milestones = useMemo(() => buildMilestoneSequence(8), [flags]);
  const yearHistogram = useMemo<YearHighlight[]>(
    () => buildYearHighlights(),
    [flags],
  );

  const [playOpen, setPlayOpen] = useState(false);
  const [activeStory, setActiveStory] = useState<PlaybackStory | null>(null);

  const playTimeline = (t: LegacyTimeline) => {
    setActiveStory({
      label: t.label,
      subtitle:
        t.yearRange.from && t.yearRange.to
          ? `Based on ${t.memoryCount} memories from ${t.yearRange.from}–${t.yearRange.to}`
          : `Based on ${t.memoryCount} memor${t.memoryCount === 1 ? 'y' : 'ies'}`,
      slides: t.slides,
    });
    setPlayOpen(true);
  };

  const playOverall = () => {
    const t = buildLegacyTimeline(undefined, { maxSlidesPerTimeline: 14 });
    if (!t) return;
    setActiveStory({
      label: 'Your Family Story',
      subtitle:
        t.yearRange.from && t.yearRange.to
          ? `Based on ${t.memoryCount} memories from ${t.yearRange.from}–${t.yearRange.to}`
          : `Based on ${t.memoryCount} memor${t.memoryCount === 1 ? 'y' : 'ies'}`,
      slides: t.slides,
    });
    setPlayOpen(true);
  };

  const totalMemories = journey?.totals.memoryCount ?? 0;
  const totalsRange = journey?.totals.yearRange;

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-fuchsia-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="text-slate-700"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Back to dashboard
          </Button>
          <Badge className="bg-violet-100 text-violet-800 border-violet-200">
            <ShieldCheck className="w-3.5 h-3.5 mr-1" />
            Owner / Admin only
          </Badge>
        </div>

        <div className="flex items-center gap-3 mb-2">
          <Film className="w-7 h-7 text-violet-700" />
          <h1 className="font-serif text-3xl md:text-4xl font-bold text-slate-900">
            Legacy Viewer
          </h1>
          <span className="text-[10px] uppercase tracking-wide font-semibold text-violet-700 bg-violet-100 px-2 py-0.5 rounded-full">
            Phase 6D · Beta
          </span>
        </div>
        <p className="text-sm text-slate-600 mb-8 max-w-3xl">
          Read-only preview of the Legacy Presentation Layer. Toggle flags to
          test individual surfaces in this browser. Nothing on this page writes
          to the database, mutates a memory, or invents family history.
        </p>

        {/* Stability strip */}
        <div className="mb-8 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            Flag toggles below persist in <strong>this browser only</strong>.
            For project-wide rollout, set the matching{' '}
            <code>VITE_ENABLE_AI_*</code> env vars and redeploy.
          </div>
        </div>

        {/* Flag toggles */}
        <Card className="mb-8 border-violet-100">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-violet-600" />
              Phase 6D Feature Flags
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {LEGACY_FLAGS.map((f) => (
                <label
                  key={f.key}
                  htmlFor={`flag-${f.key}`}
                  className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:border-violet-300 cursor-pointer transition-colors"
                >
                  <Switch
                    id={`flag-${f.key}`}
                    checked={flags[f.key]}
                    onCheckedChange={(v) => {
                      setAIFlagLocal(f.key, v);
                      refreshFlags();
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <Label
                      htmlFor={`flag-${f.key}`}
                      className="text-sm font-semibold text-slate-900 cursor-pointer"
                    >
                      {f.label}
                    </Label>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {f.description}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <Card className="mb-8 border-violet-100">
          <CardHeader>
            <CardTitle className="text-base">Legacy Timeline Snapshot</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Indexed memories" value={String(totalMemories)} />
              <Stat
                label="Year range"
                value={
                  totalsRange?.from && totalsRange?.to
                    ? `${totalsRange.from}–${totalsRange.to}`
                    : '—'
                }
              />
              <Stat label="Eras" value={String(eras.length)} />
              <Stat label="Themes" value={String(themes.length)} />
              <Stat label="People timelines" value={String(people.length)} />
              <Stat label="Milestones" value={String(milestones.length)} />
              <Stat
                label="Active years"
                value={String(yearHistogram.length)}
              />
              <Stat
                label="Master flag"
                value={isLegacyTimelineEnabled() ? 'ON' : 'OFF'}
              />
            </div>
            {isStoryPlaybackEnabled() && totalMemories > 0 && (
              <Button
                onClick={playOverall}
                className="bg-gradient-to-r from-violet-600 via-fuchsia-500 to-pink-500 text-white"
              >
                <PlayCircle className="w-4 h-4 mr-1.5" />
                Play overall family story
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Era timelines */}
        {eras.length > 0 && (
          <Card className="mb-8 border-violet-100">
            <CardHeader>
              <CardTitle className="text-base">Timelines by Era</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {eras.map((t) => (
                <TimelineRow key={t.bucket} t={t} onPlay={playTimeline} />
              ))}
            </CardContent>
          </Card>
        )}

        {/* Theme timelines */}
        {themes.length > 0 && (
          <Card className="mb-8 border-violet-100">
            <CardHeader>
              <CardTitle className="text-base">Timelines by Theme</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {themes.map((t) => (
                <TimelineRow key={t.bucket} t={t} onPlay={playTimeline} />
              ))}
            </CardContent>
          </Card>
        )}

        {/* Live previews of public 6D cards */}
        {isLegacyViewerEnabled() && (
          <div className="space-y-2">
            <h2 className="font-serif text-xl font-bold text-slate-900 mt-4 mb-1">
              Live previews
            </h2>
            <p className="text-xs text-slate-500 mb-3">
              These render exactly as they will on the dashboard. Each card
              renders null when its dependent flag is OFF.
            </p>
            <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
              <MemorySpotlightCard />
            </div>
            <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
              <FamilyDocumentaryCard />
            </div>
            <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
              <RelationshipJourneyCard />
            </div>
          </div>
        )}

        <p className="text-[11px] text-slate-400 mt-8">
          STABILITY.md §13–§14: Phase 6D is read-only. No memory rows, captions,
          uploads, billing, or relationship engine state is mutated by this
          page or by any of its features.
        </p>
      </div>

      <StoryPlaybackModal
        open={playOpen}
        onOpenChange={setPlayOpen}
        story={activeStory}
      />
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-lg bg-white border border-slate-200 p-3">
    <div className="text-[10px] uppercase tracking-wide text-slate-500">
      {label}
    </div>
    <div className="text-lg font-bold text-slate-900 mt-0.5">{value}</div>
  </div>
);

const TimelineRow: React.FC<{
  t: LegacyTimeline;
  onPlay: (t: LegacyTimeline) => void;
}> = ({ t, onPlay }) => (
  <div className="flex items-center justify-between gap-3 p-2 rounded-lg hover:bg-violet-50 transition-colors">
    <div className="flex-1 min-w-0">
      <div className="text-sm font-semibold text-slate-900 truncate">
        {t.label}
      </div>
      <div className="text-[11px] text-slate-500">
        {t.memoryCount} memor{t.memoryCount === 1 ? 'y' : 'ies'}
        {t.yearRange.from && t.yearRange.to
          ? ` · ${t.yearRange.from}–${t.yearRange.to}`
          : ''}
      </div>
    </div>
    {isStoryPlaybackEnabled() && (
      <Button
        size="sm"
        variant="outline"
        onClick={() => onPlay(t)}
        className="border-violet-200 text-violet-700"
      >
        <PlayCircle className="w-4 h-4 mr-1" />
        Play
      </Button>
    )}
  </div>
);

export default LegacyViewer;
